
import { AppLanguage } from "@/lib/i18n";
import { Crop } from "@/lib/types/Crop";
import { gettersInstance, propsInstance } from "@/lib/util";
import { Component, MixedVue, mixins } from "@/vue";
import { Watch } from "vue-property-decorator";
import { mapGetters } from "vuex";
import MultiSelect from "../MultiSelect.vue";

const Props = propsInstance<{
  compact?: boolean;
  small?: boolean;
}>({
  compact: { type: Boolean, default: false, required: false },
  small: { type: Boolean, default: false, required: false },
});

const StoreGetters = gettersInstance(["cropsForCurrentSeason", "totalCrops", "selectedCropId", "currentSeasonId", "parcelsForCurrentSeason"]);

@Component({
  name: "CropFilter",
  components: { MultiSelect },
})
export default class CropFilter extends mixins(MixedVue, Props, StoreGetters) {
  static components: {
    MultiSelect: typeof MultiSelect;
  };

  showAllCrops = false;
  maxCropsShown = 5;

  get displayedCrops() {
    // Check if parcels with unspecified crop really exist
    let shownCrops: Crop[];
    let unspecifiedCrop = this.parcelsForCurrentSeason.find((parcel) => !this.getCropIntervalCrop(parcel.crop_intervals));
    if (!unspecifiedCrop) {
      shownCrops = this.cropsForCurrentSeason.filter((crop) => crop.id != "null");
    } else {
      shownCrops = [...this.cropsForCurrentSeason];
    }
    if (this.showAllCrops) {
      return shownCrops;
    }
    return shownCrops.slice(0, this.maxCropsShown);
  }

  @Watch("currentSeasonId")
  currentSeasonIdChanged() {
    this.$store.commit("setCropSelected", null);
  }

  mounted() {
    this.setSelectedCropsFromRoute();
    if (this.cropsForCurrentSeason.length <= this.maxCropsShown) {
      this.showAllCrops = true;
    }
  }

  getCropLabel(crop: Crop) {
    const cropName = crop.name[this.$language.current as AppLanguage];
    const apiaCode = crop.apia_code || crop.system_links?.find((link) => link.name === "APIA")?.crop_code;
    return apiaCode ? `${cropName} (${apiaCode})` : cropName;
  }

  isSelected(crop: Crop) {
    return crop.id === this.selectedCropId;
  }

  setSelectedCropsFromRoute() {
    if (!this.$route.query.crop) return;
    this.$store.commit("setCropSelected", this.$route.query.crop as string);

    // Show all crops, in case the ones selected in the URL are hidden by default.
    this.showAllCrops = true;
  }

  @Watch("selectedCropId")
  syncRoute() {
    this.$router.replace({
      name: this.$route.name,
      params: {
        ...this.$route.params,
      },
      query: {
        ...this.$route.query,
        crop: this.selectedCropId,
      },
    });
  }

  setCropSelected(crop: Crop) {
    if (crop.id === this.selectedCropId) {
      this.$store.commit("setCropSelected", null);
    } else {
      this.$store.commit("setCropSelected", crop.id);
    }
    this.syncRoute();
  }
}
